/**
 * Service Date Utility Functions
 * 
 * SINGLE SOURCE OF TRUTH for calculating next service dates across the platform
 * 
 * This ensures consistent logic across:
 * - All Customers table
 * - Customer Details dialog (all service sections)
 * - Any other components that display next service dates
 */

import { ExistingCustomer } from '../types';
import { getTodayInUserTimezone } from './dateUtils';

function toUtcMidnight(dateInput: string | Date): Date | null {
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return null;
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0));
}

function getTodayUtcMidnight(): Date {
  const todayStr = getTodayInUserTimezone();
  const [y, m, day] = todayStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, day, 0, 0, 0, 0));
}

/**
 * Pick the authoritative ServiceRequest for a category.
 * Prefer active upcoming scheduled/in-progress, then accepted with a future date.
 * Never prefer an arbitrary first match (often an old completed/past row).
 */
export const selectBestServiceRequestForCategory = (
  serviceRequests: any[] | undefined | null,
  category: 'mowing' | 'fertilizing' | 'snow_removal'
): any | null => {
  if (!Array.isArray(serviceRequests) || serviceRequests.length === 0) return null;

  const matching = serviceRequests.filter((sr: any) => sr?.serviceType?.category === category);
  if (matching.length === 0) return null;

  const todayUTC = getTodayUtcMidnight();

  const withFutureDate = (statuses: string[]) =>
    matching
      .filter((sr: any) => statuses.includes(sr.status) && sr.scheduledDate)
      .map((sr: any) => ({ sr, dateUTC: toUtcMidnight(sr.scheduledDate) }))
      .filter((x) => x.dateUTC && x.dateUTC.getTime() >= todayUTC.getTime())
      .sort((a, b) => a.dateUTC!.getTime() - b.dateUTC!.getTime());

  const upcomingScheduled = withFutureDate(['scheduled', 'in-progress']);
  if (upcomingScheduled.length > 0) return upcomingScheduled[0].sr;

  const upcomingAccepted = withFutureDate(['accepted']);
  if (upcomingAccepted.length > 0) return upcomingAccepted[0].sr;

  // Last resort: most recently updated / latest scheduledDate among matching (still may be past)
  const sorted = [...matching].sort((a: any, b: any) => {
    const ad = toUtcMidnight(a.scheduledDate || a.updatedAt || 0)?.getTime() || 0;
    const bd = toUtcMidnight(b.scheduledDate || b.updatedAt || 0)?.getTime() || 0;
    return bd - ad;
  });
  return sorted[0] || null;
};

/**
 * Get service frequency from multiple sources (serviceRequest, customer.servicePricing, acceptedBid)
 * This is the authoritative way to determine service frequency
 */
export const getServiceFrequency = (
  customer: ExistingCustomer | null | undefined,
  category: 'mowing' | 'fertilizing' | 'snow_removal',
  serviceRequest: any
): string | null => {
  // Handle null/undefined customer
  if (!customer) {
    console.log('[GET-SERVICE-FREQUENCY] Customer is null/undefined, falling back to serviceRequest.frequency');
    // Fallback to serviceRequest.frequency if customer is not available
    if (serviceRequest?.frequency && serviceRequest.frequency !== 'seasonal-contract') {
      return serviceRequest.frequency;
    }
    return null;
  }

  // CRITICAL: For bid-accepted customers, Bid.servicePricing is the PRIMARY source of truth
  // Check Bid FIRST if serviceRequest has an acceptedBid (bid-accepted customers)
  // This prevents using incorrect data from ExistingCustomer.servicePricing
  // Priority 0: Check Bid.servicePricing FIRST for bid-accepted customers
  if (serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => {
      if (category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
      if (category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
      if (category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
      return false;
    });

    // CRITICAL: Only check seasonalContractDetails.serviceFrequency if this is actually a seasonal contract
    // For non-seasonal contracts, use frequency directly (don't check seasonalContractDetails even if it exists)
    const isSeasonalContract = matchingServicePricing?.isSeasonalContract === true || matchingServicePricing?.frequency === 'seasonal-contract';
    
    if (isSeasonalContract && matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
      // For seasonal contracts, check serviceFrequency FIRST (before frequency)
      // This ensures per-instance seasonal contracts return 'per-instance', not 'seasonal-contract'
      console.log('[GET-SERVICE-FREQUENCY] ✅ Found serviceFrequency in Bid.servicePricing (bid-accepted customer, seasonal contract):', {
        category,
        serviceFrequency: matchingServicePricing.seasonalContractDetails.serviceFrequency,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
        note: 'Bid is source of truth for bid-accepted customers - seasonal contract'
      });
      return matchingServicePricing.seasonalContractDetails.serviceFrequency;
    }

    // For non-seasonal contracts, use frequency from Bid directly
    // Don't check seasonalContractDetails even if it exists (it might be leftover incorrect data)
    if (matchingServicePricing?.frequency && matchingServicePricing.frequency !== 'seasonal-contract') {
      console.log('[GET-SERVICE-FREQUENCY] ✅ Found frequency in Bid.servicePricing (bid-accepted customer, non-seasonal):', {
        category,
        frequency: matchingServicePricing.frequency,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
        note: 'Bid is source of truth for bid-accepted customers - non-seasonal contract'
      });
      return matchingServicePricing.frequency;
    }
  }

  // CRITICAL: For onboarded customers (no acceptedBid), customer.servicePricing is the PRIMARY source of truth
  // Check this SECOND, only if no acceptedBid exists
  // Priority 1: Check customer.servicePricing for onboarded customers
  // Type assertion: servicePricing may have seasonalContractDetails even if TypeScript doesn't know it
  if (category === 'mowing' && customer?.servicePricing?.lawnMowingTrimming) {
    const mowingPricing = customer.servicePricing.lawnMowingTrimming as any;
    console.log('[GET-SERVICE-FREQUENCY] Checking customer.servicePricing.lawnMowingTrimming:', {
      category: 'mowing',
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
      hasMowingPricing: !!mowingPricing,
      frequency: mowingPricing?.frequency,
      hasSeasonalContractDetails: !!mowingPricing?.seasonalContractDetails,
      serviceFrequency: mowingPricing?.seasonalContractDetails?.serviceFrequency,
      fullMowingPricing: mowingPricing
    });
    // For seasonal contracts, check seasonalContractDetails.serviceFrequency first
    if (mowingPricing.frequency === 'seasonal-contract' && mowingPricing.seasonalContractDetails?.serviceFrequency) {
      const serviceFreq = mowingPricing.seasonalContractDetails.serviceFrequency;
      console.log('[GET-SERVICE-FREQUENCY] ✅ Found serviceFrequency in customer.servicePricing (onboarded):', {
        category: 'mowing',
        serviceFrequency: serviceFreq,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown'
      });
      return serviceFreq;
    }
    // If not seasonal-contract, return the frequency directly
    if (mowingPricing.frequency && mowingPricing.frequency !== 'seasonal-contract') {
      return mowingPricing.frequency;
    }
    
  } else if (category === 'fertilizing' && customer?.servicePricing?.weedControlFertilizing) {
    const fertilizingPricing = customer.servicePricing.weedControlFertilizing as any;
    console.log('[GET-SERVICE-FREQUENCY] Checking customer.servicePricing.weedControlFertilizing:', {
      category: 'fertilizing',
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
      hasFertilizingPricing: !!fertilizingPricing,
      frequency: fertilizingPricing?.frequency,
      hasSeasonalContractDetails: !!fertilizingPricing?.seasonalContractDetails,
      serviceFrequency: fertilizingPricing?.seasonalContractDetails?.serviceFrequency,
      fullFertilizingPricing: fertilizingPricing
    });
    // For seasonal contracts, check seasonalContractDetails.serviceFrequency first
    if (fertilizingPricing.frequency === 'seasonal-contract' && fertilizingPricing.seasonalContractDetails?.serviceFrequency) {
      const serviceFreq = fertilizingPricing.seasonalContractDetails.serviceFrequency;
      console.log('[GET-SERVICE-FREQUENCY] ✅ Found serviceFrequency in customer.servicePricing (onboarded):', {
        category: 'fertilizing',
        serviceFrequency: serviceFreq,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown'
      });
      return serviceFreq;
    }
    // If not seasonal-contract, return the frequency directly
    if (fertilizingPricing.frequency && fertilizingPricing.frequency !== 'seasonal-contract') {
      return fertilizingPricing.frequency;
    }
  } else if (category === 'snow_removal' && customer?.servicePricing?.snowRemoval) {
    const snowPricing = customer.servicePricing.snowRemoval as any;
    console.log('[GET-SERVICE-FREQUENCY] Checking customer.servicePricing.snowRemoval:', {
      category: 'snow_removal',
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
      hasSnowPricing: !!snowPricing,
      frequency: snowPricing?.frequency,
      hasSeasonalContractDetails: !!snowPricing?.seasonalContractDetails,
      serviceFrequency: snowPricing?.seasonalContractDetails?.serviceFrequency,
      fullSnowPricing: snowPricing
    });
    // For seasonal contracts, check seasonalContractDetails.serviceFrequency first
    if (snowPricing.frequency === 'seasonal-contract' && snowPricing.seasonalContractDetails?.serviceFrequency) {
      const serviceFreq = snowPricing.seasonalContractDetails.serviceFrequency;
      console.log('[GET-SERVICE-FREQUENCY] ✅ Found serviceFrequency in customer.servicePricing (onboarded):', {
        category: 'snow_removal',
        serviceFrequency: serviceFreq,
        customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown'
      });
      return serviceFreq;
    }
    // If not seasonal-contract, return the frequency directly
    if (snowPricing.frequency && snowPricing.frequency !== 'seasonal-contract') {
      return snowPricing.frequency;
    }
  } else {
    // Log when customer.servicePricing doesn't exist for this category
    console.log('[GET-SERVICE-FREQUENCY] ⚠️ customer.servicePricing not found for category:', {
      category,
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
      hasServicePricing: !!customer?.servicePricing,
      hasLawnMowingTrimming: !!customer?.servicePricing?.lawnMowingTrimming,
      hasWeedControlFertilizing: !!customer?.servicePricing?.weedControlFertilizing,
      hasSnowRemoval: !!customer?.servicePricing?.snowRemoval
    });
  }

  // Priority 2: serviceRequest.frequency (fallback if Bid data is missing)
  if (serviceRequest?.frequency && serviceRequest.frequency !== 'seasonal-contract') {
    return serviceRequest.frequency;
  }

  // Fallback: If serviceRequest.frequency is 'seasonal-contract' but we couldn't find serviceFrequency, return it
  // This should only happen if customer.servicePricing doesn't have the data (data issue)
  if (serviceRequest?.frequency === 'seasonal-contract') {
    console.warn('[GET-SERVICE-FREQUENCY] ⚠️ serviceRequest.frequency is seasonal-contract but couldn\'t find serviceFrequency:', {
      category,
      customerName: customer ? `${customer.firstName} ${customer.lastName}` : 'Unknown',
      hasServicePricing: !!customer?.servicePricing,
      hasAcceptedBid: !!serviceRequest?.acceptedBid,
      note: 'This may indicate a data issue - customer.servicePricing should have seasonalContractDetails.serviceFrequency for onboarded customers'
    });
    return 'seasonal-contract';
  }

  return null;
};

/**
 * Get the next service date for a service category
 * 
 * SINGLE SOURCE OF TRUTH - Use this function everywhere instead of duplicating logic
 * 
 * @param customer - The customer object
 * @param category - Service category ('mowing', 'fertilizing', 'snow_removal')
 * @param serviceRequest - The service request for this category (can be null if not found)
 * @param customerServiceRequests - All service requests for this customer (for finding completedDate in monthly services)
 * @returns Next service date as YYYY-MM-DD string, or null if "Per Instance" or no date
 */
export const getNextServiceDate = (
  customer: ExistingCustomer,
  category: 'mowing' | 'fertilizing' | 'snow_removal',
  serviceRequest: any,
  customerServiceRequests: any[] = []
): string | null => {
  if (!customer) return null;

  // Prefer the best matching SR from the full list when available (avoids stale .find() first-match).
  if (Array.isArray(customerServiceRequests) && customerServiceRequests.length > 0) {
    const best = selectBestServiceRequestForCategory(customerServiceRequests, category);
    if (best) {
      serviceRequest = best;
    }
  }

  // Get service frequency using the shared helper
  const serviceFrequency = getServiceFrequency(customer, category, serviceRequest);

  if (!serviceFrequency) {
    console.warn('[SERVICE-DATE-UTILS] No service frequency found:', { category, hasServiceRequest: !!serviceRequest });
    return null;
  }

  // Handle seasonal contracts - check actual serviceFrequency
  let actualServiceFrequency = serviceFrequency;
  let isActuallyPerInstance = serviceFrequency === 'per-instance';
  
  if (serviceFrequency === 'seasonal-contract' && serviceRequest?.acceptedBid?.servicePricing) {
    const bid = serviceRequest.acceptedBid;
    if (bid.servicePricing && Array.isArray(bid.servicePricing)) {
      const matchingServicePricing = bid.servicePricing.find((sp: any) => {
        if (category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
        if (category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
        if (category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
        return false;
      });
      
      if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
        actualServiceFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
        if (actualServiceFrequency === 'per-instance') {
          isActuallyPerInstance = true;
        }
      }
    }
  }

  // PER-INSTANCE SERVICES: Only show date if manually scheduled for today or future
  // CRITICAL: For per-instance services, after completion, if scheduledDate is today (not future), show "Per Instance"
  const isSnowRemoval = category === 'snow_removal';
  
  if (isActuallyPerInstance || actualServiceFrequency === 'per-instance') {
    // For per-instance services (including snow removal): Show scheduledDate if it's today or in the future
    // BUT: If service was completed TODAY and scheduledDate is today, return null (show "Per Instance")
    // This allows:
    // - Scheduled services for today (not completed) → show today's date
    // - Scheduled services for future → show future date
    // - Completed services for today → show "Per Instance"
    // CRITICAL: Check if service was completed TODAY, not just if it has a completedDate (which might be from yesterday)
    // For per-instance services, a serviceRequest can have:
    //   - completedDate from a previous completion (e.g., yesterday)
    //   - scheduledDate for today (new scheduled service, not yet completed)
    //   - status: 'scheduled' (not completed yet)
    // In this case, we should show today's date, not "Per Instance"
    const todayInUserTZ = getTodayInUserTimezone();
    const [todayYear, todayMonth, todayDay] = todayInUserTZ.split('-').map(Number);
    const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));
    
    // Check if service was completed TODAY (not just if it has a completedDate from any day)
    let isCompletedToday = false;
    if (serviceRequest?.completedDate) {
      const completedDate = new Date(serviceRequest.completedDate);
      const completedYear = completedDate.getUTCFullYear();
      const completedMonth = completedDate.getUTCMonth();
      const completedDay = completedDate.getUTCDate();
      const completedDateUTC = new Date(Date.UTC(completedYear, completedMonth, completedDay, 0, 0, 0, 0));
      isCompletedToday = completedDateUTC.getTime() === todayUTC.getTime();
    }
    
    // Also check status - if status is 'completed', it was completed (but might be from a different day)
    const isStatusCompleted = serviceRequest?.status === 'completed';
    
    if (serviceRequest?.scheduledDate) {
      const scheduledDate = new Date(serviceRequest.scheduledDate);
      const scheduledYear = scheduledDate.getUTCFullYear();
      const scheduledMonth = scheduledDate.getUTCMonth();
      const scheduledDay = scheduledDate.getUTCDate();

      const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));

      // If service was completed TODAY and scheduledDate is today, return null (show "Per Instance")
      // CRITICAL: Only return null if it was completed TODAY, not if it just has a completedDate from yesterday
      if (isCompletedToday && scheduledDateUTC.getTime() === todayUTC.getTime()) {
        return null; // Will display as "Per Instance"
      }
      
      // Show scheduled date if it's today (not completed today) or in the future
      if (scheduledDateUTC >= todayUTC) {
        return serviceRequest.scheduledDate;
      }
    }

    // For non-completed per-instance services, only show date if scheduled for today or future (manually scheduled)
    if (serviceRequest?.scheduledDate) {
      const scheduledDate = new Date(serviceRequest.scheduledDate);
      const scheduledYear = scheduledDate.getUTCFullYear();
      const scheduledMonth = scheduledDate.getUTCMonth();
      const scheduledDay = scheduledDate.getUTCDate();

      const todayInUserTZ = getTodayInUserTimezone();
      const [todayYear, todayMonth, todayDay] = todayInUserTZ.split('-').map(Number);

      const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));
      const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));

      if (scheduledDateUTC >= todayUTC) {
        return serviceRequest.scheduledDate; // Manually scheduled
      }
    }

    return null; // Show "Per Instance"
  }

  // MONTHLY SERVICES: Use scheduledDate as primary source (backend already calculated correctly)
  // Only recalculate if scheduledDate is missing or clearly incorrect
  // CRITICAL: Only use scheduledDate if the service has actually been scheduled (status is 'scheduled')
  if (actualServiceFrequency === 'monthly' && serviceRequest?.scheduledDate && serviceRequest?.status === 'scheduled') {
    console.log('[SERVICE-DATE-UTILS] Monthly service detected:', {
      category,
      scheduledDate: serviceRequest.scheduledDate,
      hasCompletedDate: !!serviceRequest?.completedDate,
      completedDate: serviceRequest?.completedDate,
      status: serviceRequest?.status,
      note: 'Using scheduledDate as primary source (backend calculated this correctly)'
    });

    // CRITICAL: For monthly services, trust the backend's calculated scheduledDate
    // The backend uses the correct base date (original scheduled date for one-time changes)
    // and the correct recurringDayOfMonth (from firstServiceDate for onboarded customers)
    // Only recalculate if scheduledDate is clearly wrong (e.g., in the past)
    const scheduledDate = new Date(serviceRequest.scheduledDate);
    const todayStr = getTodayInUserTimezone();
    const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
    const scheduledYear = scheduledDate.getUTCFullYear();
    const scheduledMonth = scheduledDate.getUTCMonth();
    const scheduledDay = scheduledDate.getUTCDate();
    const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));
    const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));

    // If scheduledDate is today or in the future, use it (backend calculated it correctly)
    if (scheduledDateUTC >= todayUTC) {
      console.log('[SERVICE-DATE-UTILS] Using backend-calculated scheduledDate for monthly service:', {
        category,
        scheduledDate: scheduledDateUTC.toISOString().split('T')[0],
        note: 'Backend already calculated this correctly using original scheduled date and recurringDayOfMonth'
      });
      return serviceRequest.scheduledDate;
    }

    // Only recalculate if scheduledDate is in the past (shouldn't happen, but handle edge case)
    console.warn('[SERVICE-DATE-UTILS] Monthly service scheduledDate is in the past, recalculating:', {
      category,
      scheduledDate: scheduledDateUTC.toISOString().split('T')[0],
      today: todayUTC.toISOString().split('T')[0]
    });

    // Recalculation logic (fallback only)
    let completedDate = serviceRequest?.completedDate;
    if (!completedDate) {
      const requestsWithCompletedDate = customerServiceRequests.filter((sr: any) => {
        const matchesCategory = sr.serviceType?.category === category;
        const hasCompletedDate = !!sr.completedDate;
        const isCompleted = sr.status === 'completed';
        const isScheduledWithCompletedDate = sr.status === 'scheduled' && hasCompletedDate;
        return matchesCategory && hasCompletedDate && (isCompleted || isScheduledWithCompletedDate);
      });

      if (requestsWithCompletedDate.length > 0) {
        const mostRecent = requestsWithCompletedDate.reduce((latest: any, current: any) => {
          if (!latest.completedDate) return current;
          if (!current.completedDate) return latest;
          return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
        });
        completedDate = mostRecent.completedDate;
      }
    }

    if (completedDate) {
      const completedDateObj = new Date(completedDate);
      const year = completedDateObj.getUTCFullYear();
      const month = completedDateObj.getUTCMonth();
      const day = completedDateObj.getUTCDate();

      // Try to get recurringDayOfMonth from Bid
      let targetDayOfMonth: number | undefined = undefined;
      if (serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
        const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => {
          if (category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
          if (category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
          if (category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
          return false;
        });

        if (matchingServicePricing?.recurringDayOfMonth !== undefined && matchingServicePricing?.recurringDayOfMonth !== null) {
          targetDayOfMonth = matchingServicePricing.recurringDayOfMonth;
        }
      }

      // FALLBACK: For onboarded customers without bids, extract from customer.servicePricing or firstServiceDate
      if (targetDayOfMonth === undefined) {
        if (category === 'fertilizing' && customer.servicePricing?.weedControlFertilizing) {
          const fertilizingPricing = customer.servicePricing.weedControlFertilizing as any;
          if (fertilizingPricing.recurringDayOfMonth !== undefined && fertilizingPricing.recurringDayOfMonth !== null) {
            targetDayOfMonth = fertilizingPricing.recurringDayOfMonth;
            console.log('[SERVICE-DATE-UTILS] Using recurringDayOfMonth from customer.servicePricing (onboarded customer):', targetDayOfMonth);
          }
        }
        
        // Last resort: use completedDate's day (not ideal)
        // NOTE: We no longer use firstServiceDate as fallback because it's shared across services and can show wrong data
        if (targetDayOfMonth === undefined) {
          targetDayOfMonth = day;
          console.warn('[SERVICE-DATE-UTILS] Using completedDate day as fallback (not ideal):', targetDayOfMonth);
        }
      }

      // Calculate next date: add 1 month, use target day
      let nextYear = year;
      let nextMonth = month + 1;
      const daysInMonth = new Date(Date.UTC(nextYear, nextMonth + 1, 0)).getUTCDate();
      const validDayOfMonth = Math.min(targetDayOfMonth, daysInMonth);
      const nextDate = new Date(Date.UTC(nextYear, nextMonth, validDayOfMonth, 0, 0, 0, 0));

      // Weekend adjustment: if falls on Saturday or Sunday, move to preceding Friday
      const dayOfWeekResult = nextDate.getUTCDay();
      if (dayOfWeekResult === 6) { // Saturday
        nextDate.setUTCDate(nextDate.getUTCDate() - 1); // Move to Friday
      } else if (dayOfWeekResult === 0) { // Sunday
        nextDate.setUTCDate(nextDate.getUTCDate() - 2); // Move to Friday
      }

      const nextDateUTC = new Date(Date.UTC(
        nextDate.getUTCFullYear(),
        nextDate.getUTCMonth(),
        nextDate.getUTCDate(),
        0, 0, 0, 0
      ));

      console.log('[SERVICE-DATE-UTILS] Recalculated date for monthly service (fallback):', nextDateUTC.toISOString().split('T')[0]);
      return nextDateUTC.toISOString().split('T')[0];
    }
  }

  // For scheduled/pending jobs, use the scheduledDate (SINGLE POINT OF TRUTH)
  // CRITICAL: Only use scheduledDate if the service has actually been scheduled (status is 'scheduled')
  // Don't use scheduledDate for 'accepted' services that haven't been scheduled yet
  if (serviceRequest?.scheduledDate && serviceRequest?.status === 'scheduled') {
    // CRITICAL SAFETY CHECK: Verify it's NOT a per-instance service before returning
    if (isActuallyPerInstance || actualServiceFrequency === 'per-instance') {
      // If it has completedDate, it was completed, so show "Per Instance"
      if (serviceRequest?.completedDate || serviceRequest?.status === 'completed') {
        return null;
      }

      // If it's scheduled for today or future, it was manually scheduled, so show the date
      const scheduledDate = new Date(serviceRequest.scheduledDate);
      const todayStr = getTodayInUserTimezone();
      const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
      const scheduledYear = scheduledDate.getUTCFullYear();
      const scheduledMonth = scheduledDate.getUTCMonth();
      const scheduledDay = scheduledDate.getUTCDate();
      const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));
      const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));

      if (scheduledDateUTC >= todayUTC) {
        return serviceRequest.scheduledDate; // Manually scheduled
      }
      return null; // Show "Per Instance"
    }

    // For non-per-instance services, return scheduledDate
    const scheduledDate = new Date(serviceRequest.scheduledDate);
    const todayStr = getTodayInUserTimezone();
    const [todayYear, todayMonth, todayDay] = todayStr.split('-').map(Number);
    const scheduledYear = scheduledDate.getUTCFullYear();
    const scheduledMonth = scheduledDate.getUTCMonth();
    const scheduledDay = scheduledDate.getUTCDate();
    const scheduledDateUTC = new Date(Date.UTC(scheduledYear, scheduledMonth, scheduledDay, 0, 0, 0, 0));
    const todayUTC = new Date(Date.UTC(todayYear, todayMonth - 1, todayDay, 0, 0, 0, 0));

    // Only return if scheduled for today or in the future
    if (scheduledDateUTC >= todayUTC) {
      return serviceRequest.scheduledDate;
    }
  }
  
  // CRITICAL: For services that haven't been scheduled yet (status is 'accepted' or no scheduledDate),
  // don't calculate a date - return null to show "Not scheduled"
  if (!serviceRequest?.scheduledDate || serviceRequest?.status === 'accepted') {
    return null;
  }

  // For per-instance services, never use customer.nextServiceDate as fallback
  if (isActuallyPerInstance || actualServiceFrequency === 'per-instance') {
    return null; // Will display as "Per Instance"
  }

  // Fallback: Use customer.nextServiceDate ONLY when it is today/future.
  // Never surface a stale past EC date (common for multi-service customers where skip/complete
  // intentionally does not update ExistingCustomer.nextServiceDate).
  if (customer.nextServiceDate) {
    const nextDateUTC = toUtcMidnight(customer.nextServiceDate);
    const todayUTC = getTodayUtcMidnight();
    if (nextDateUTC && nextDateUTC.getTime() >= todayUTC.getTime()) {
      return nextDateUTC.toISOString().split('T')[0];
    }
  }

  return null;
};

